import { AutoComplete, Input, Spin } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HelpText, LoadingIndicator, Wrapper } from './AddressAutocomplete.styles';

import { AddressSuggestion, useAddressAutocomplete } from '@/hooks';
import { INormalizedAddress } from '@/types';

type Props = {
  onAddressSelect: (address: INormalizedAddress) => void;
  onClear?: () => void;
  placeholder: string;
  initialValue?: string;
  onError?: (error: string | null) => void;
};

export const AddressAutocomplete: React.FC<Props> = ({
  onAddressSelect,
  onClear,
  placeholder,
  initialValue,
  onError
}) => {
  const { t } = useTranslation('DefaultTranslations');
  const { suggestions, isLoading, searchAddresses, clearSuggestions, error } =
    useAddressAutocomplete();
  const previousErrorRef = useRef<string | null>(null);

  useEffect(() => {
    if (error !== previousErrorRef.current && error) {
      previousErrorRef.current = error;
      onError?.(error);
    } else if (!error && previousErrorRef.current) {
      previousErrorRef.current = null;
      onError?.(null);
    }
  }, [error, onError]);
  const [inputValue, setInputValue] = useState(initialValue ?? '');
  const hasSelectedAddress = useRef(!!initialValue);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    if (initialValue) {
      setInputValue(initialValue);
      hasSelectedAddress.current = true;
      hasInitialized.current = true;
    }
  }, [initialValue]);

  const handleSearch = useCallback(
    (searchText: string) => {
      if (hasSelectedAddress.current && searchText !== inputValue) {
        hasSelectedAddress.current = false;
        onClear?.();
      }
      setInputValue(searchText);
      searchAddresses(searchText);
    },
    [searchAddresses, inputValue, onClear]
  );

  const handleSelect = useCallback(
    (_: string, option: { suggestion: AddressSuggestion }) => {
      const suggestion = option.suggestion;
      setInputValue(suggestion.displayName);
      hasSelectedAddress.current = true;
      onAddressSelect(suggestion.normalizedAddress);
      clearSuggestions();
    },
    [onAddressSelect, clearSuggestions]
  );

  const handleBlur = useCallback(() => {
    setTimeout(() => {
      clearSuggestions();
    }, 200);
  }, [clearSuggestions]);

  const options = useMemo(
    () =>
      suggestions.map(suggestion => ({
        value: suggestion.id,
        label: suggestion.displayName,
        suggestion
      })),
    [suggestions]
  );
  return (
    <Wrapper>
      <AutoComplete
        value={inputValue}
        options={options}
        onSearch={handleSearch}
        onSelect={handleSelect}
        onBlur={handleBlur}
        style={{ width: '100%' }}
        filterOption={false}>
        <Input placeholder={placeholder || t('SEARCH_ADDRESS')} />
      </AutoComplete>
      <LoadingIndicator $visible={isLoading}>
        <Spin size="small" />
      </LoadingIndicator>
      <HelpText>{t('ADDRESS_AUTOCOMPLETE_HELP')}</HelpText>
    </Wrapper>
  );
};
